import { Stack, Typography } from "@mui/material";
import { PlaybookTitle } from "./PlaybookTitle";
import { TextBlock } from "./TextBlock";
import { CardList } from "./CardList";
import { TitleBlock } from "./TitleBlock";
import { OutreachResearchType } from "../../../../types";
import { useEffect } from "react";
import { H4Component } from "./H4Component";
import { SDRActions } from "./SDRActions";
import { CardWrapper } from "./CardWrapper";
import { SmallBgTopSquereIcon } from "../../../images/SmallBgTopSquereIcon";

const DisclaimerBlock = () => {
  return (
    <Stack
      sx={{
        padding: "8px 16px",
        border: "1px solid #FFD5A1",
        borderRadius: "6px",
        backgroundColor: "#FFF7E5",
      }}
    >
      <Typography
        sx={{
          fontSize: "16px",
          color: "#464646",
          lineHeight: "24px",
        }}
      >
        <span style={{ fontWeight: 600 }}>Disclaimer: </span>
        This list is compiled from public data and may not be fully accurate.
        Please confirm software use with the prospect before mentioning it
        directly.
      </Typography>
    </Stack>
  );
};

const TextItem = ({
  text,
  isBold,
  maxWidth,
}: {
  text: string;
  isBold?: boolean;
  maxWidth?: string;
}) => {
  return (
    <Typography
      sx={{
        fontSize: "16px",
        lineHeight: "24px",
        fontWeight: isBold ? 500 : 400,
        color: "#232323",
        width: "100%",
        maxWidth,
      }}
    >
      {text}
    </Typography>
  );
};

const QuestionnaireItem = ({
  question,
  answer,
  isBold,
}: {
  question: string;
  answer: string;
  isBold?: boolean;
}) => {
  return (
    <Stack
      flexDirection={"row"}
      gap={"32px"}
      width={"100%"}
      justifyContent={"space-between"}
      padding={"16px 0"}
      borderBottom={"1px solid #DEDEDE"}
    >
      <TextItem text={question} isBold={isBold} maxWidth={"60%"} />
      <TextItem text={answer} isBold={true} maxWidth={"40%"} />
    </Stack>
  );
};

export const PreOutreachResearchBlock = ({
  lead_company,
  setTopic,
  linkedin_profile_analysis,
  engagement_signals_analysis,
  partner_enrichment_data_analysis,
  questionnaire_responses,
  notes,
}: OutreachResearchType & {
  lead_company: string;
  setTopic: (topic: string) => void;
}) => {
  const topic = "Part 1: Immediate Prep + Fast Track Outreach (Day 0-3)";

  useEffect(() => {
    setTopic(topic);
  }, [topic]);

  return (
    <Stack gap={"32px"} id={topic}>
      <Stack gap={"20px"}>
        <PlaybookTitle text={topic} />
        {engagement_signals_analysis && (
          <>
            <Stack gap={"8px"}>
              <H4Component text={"Objective:"} />
              <TextBlock
                text={[
                  "Rapidly digest available intelligence, align internally, and initiate outbound engagement" +
                    " while intent signals are fresh and attention is high.",
                ]}
              />
            </Stack>
            <SDRActions />
          </>
        )}
      </Stack>

      {engagement_signals_analysis && engagement_signals_analysis.summary && (
        <>
          <Typography
            component="h6"
            sx={{
              color: "#024760",
              fontSize: "22px",
              fontWeight: 600,
              lineHeight: "28px",
            }}
          >
            Prospect-Provided Intelligence
          </Typography>
          <TextBlock text={engagement_signals_analysis.summary} />
        </>
      )}

      {questionnaire_responses && (
        <Stack gap={"4px"} paddingY={"12px"}>
          <Typography
            component="h6"
            sx={{
              color: "#024760",
              fontSize: "22px",
              fontWeight: 600,
              lineHeight: "28px",
            }}
          >
            Questionnaire Response Analysis
          </Typography>
          <Stack>
            <QuestionnaireItem
              question={"Question"}
              answer={"Response"}
              isBold={true}
            />
            {questionnaire_responses.map(({ question, answer }) => (
              <QuestionnaireItem
                key={question}
                question={question}
                answer={answer}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {notes && (
        <CardWrapper padding={"24px"} isCard={true}>
          <SmallBgTopSquereIcon
            style={{
              position: "absolute",
              top: "0",
              right: "220px",
              flexDirection: "row",
            }}
          />
          <SmallBgTopSquereIcon
            style={{
              position: "absolute",
              top: "0",
              right: "88px",
              flexDirection: "row",
            }}
            color={"#9CBFDC"}
          />{" "}
          <SmallBgTopSquereIcon
            style={{
              position: "absolute",
              top: "0",
              right: "-44px",
              flexDirection: "row",
            }}
            color={"#6BA0CB"}
          />
          <Stack gap="16px">
            <Typography
              component="h4"
              sx={{
                fontSize: "18px",
                lineHeight: "22px",
                fontWeight: 600,
                color: "#333333",
              }}
            >
              Call notes:
            </Typography>
            <TextBlock text={notes} />
          </Stack>
        </CardWrapper>
      )}

      {engagement_signals_analysis && engagement_signals_analysis.keys && (
        <CardList
          title={"Key Takeaways:"}
          items={engagement_signals_analysis.keys}
        />
      )}

      {partner_enrichment_data_analysis && (
        <Stack gap={"20px"}>
          <Typography
            component="h6"
            sx={{
              color: "#024760",
              fontSize: "22px",
              fontWeight: 600,
              lineHeight: "28px",
            }}
          >
            Partner/Enrichment Data Analysis
          </Typography>

          {partner_enrichment_data_analysis.summary && (
            <TextBlock text={partner_enrichment_data_analysis.summary} />
          )}

          <TitleBlock
            title={`Technologies Potentially Utilized by ${lead_company}`}
          >
            <Stack gap={"12px"}>
              <DisclaimerBlock />
              {partner_enrichment_data_analysis &&
                partner_enrichment_data_analysis.technologies_utilized.length >
                  0 && (
                  <Stack flexDirection={"row"} gap={"8px"} flexWrap={"wrap"}>
                    {partner_enrichment_data_analysis.technologies_utilized.map(
                      (technology) => (
                        <Stack
                          key={technology}
                          alignItems={"center"}
                          justifyContent={"center"}
                          padding={"6px 12px"}
                          borderRadius={"6px"}
                          sx={{ backgroundColor: "#E8ECF0" }}
                        >
                          <span style={{ fontSize: "13px", fontWeight: 500 }}>
                            {technology}
                          </span>
                        </Stack>
                      ),
                    )}
                  </Stack>
                )}
            </Stack>
          </TitleBlock>
          {partner_enrichment_data_analysis.keys && (
            <CardList
              title={"Key Takeaways:"}
              items={partner_enrichment_data_analysis.keys}
            />
          )}
        </Stack>
      )}

      {linkedin_profile_analysis && (
        <Stack gap={"20px"}>
          <Typography
            component="h6"
            sx={{
              color: "#024760",
              fontSize: "22px",
              fontWeight: 600,
              lineHeight: "28px",
            }}
          >
            LinkedIn Profile Analysis
          </Typography>

          {linkedin_profile_analysis.linkedin_profile_interests && (
            <TitleBlock title={"Interests"}>
              <TextBlock
                text={linkedin_profile_analysis.linkedin_profile_interests}
              />
            </TitleBlock>
          )}

          {linkedin_profile_analysis.certifications_summary && (
            <TitleBlock title={"Recent Certifications"}>
              <TextBlock
                text={linkedin_profile_analysis.certifications_summary}
              />
            </TitleBlock>
          )}

          {linkedin_profile_analysis.post_topics_summary && (
            <TitleBlock title={"Recent Post Topics"}>
              <TextBlock text={linkedin_profile_analysis.post_topics_summary} />
            </TitleBlock>
          )}

          {linkedin_profile_analysis.technologies_mentioned &&
            linkedin_profile_analysis.technologies_mentioned.length > 0 && (
              <TitleBlock
                title={
                  "Technologies Mentioned in the Prospect LinkedIn Profile"
                }
              >
                <Stack gap={"20px"}>
                  <Stack gap={"12px"}>
                    <DisclaimerBlock />
                    <Stack flexDirection={"row"} gap={"8px"} flexWrap={"wrap"}>
                      {linkedin_profile_analysis.technologies_mentioned.map(
                        (technology) => (
                          <Stack
                            key={technology}
                            alignItems={"center"}
                            justifyContent={"center"}
                            padding={"6px 12px"}
                            borderRadius={"6px"}
                            sx={{ backgroundColor: "#E8ECF0" }}
                          >
                            <span style={{ fontSize: "13px", fontWeight: 500 }}>
                              {technology}
                            </span>
                          </Stack>
                        ),
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </TitleBlock>
            )}

          {linkedin_profile_analysis.keys_linkedin_profile &&
            linkedin_profile_analysis.keys_linkedin_profile.length > 0 && (
              <CardList
                title={"Key Takeaways:"}
                items={linkedin_profile_analysis.keys_linkedin_profile}
              />
            )}
        </Stack>
      )}
    </Stack>
  );
};
